import type { ReactNode } from 'react'
import {
  FilePdfOutlined,
  FilePptOutlined,
  FileTextOutlined,
  MailOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons'
import type { AdvisoryAiExportFormat } from '@/features/advisory-ai/types'

const exportBaseNames: Record<AdvisoryAiExportFormat, string> = {
  pdf: 'Adviceprep',
  'pdf-summary': 'Adviceprep (Summary)',
  ppt: 'Adviceprep',
  email: 'Adviceprep',
  video: 'Adviceprep',
}

export enum AdvisoryExportStatus {
  PENDING_EXPORT = 'PENDING_EXPORT',
  EXPORTING = 'EXPORTING',
  COMPLETED_EXPORT = 'COMPLETED_EXPORT',
  ERROR = 'ERROR',
}

export type ExportOption = {
  id: AdvisoryAiExportFormat
  title: string
  description: string
  icon: ReactNode
}

export const exportOptions: ExportOption[] = [
  {
    id: 'pdf',
    title: 'Full Advisory Report (PDF)',
    description:
      'Comprehensive report with financial data, charts, AI analysis, and recommendations organised by your agenda items. Typically 8–15 pages.',
    icon: <FilePdfOutlined className="text-2xl" />,
  },
  {
    id: 'pdf-summary',
    title: 'Executive Summary (PDF)',
    description:
      'One-page overview with key metrics, agenda highlights, and action items. Hand to the client at the start of the meeting.',
    icon: <FileTextOutlined className="text-2xl" />,
  },
  {
    id: 'ppt',
    title: 'Meeting Slide Deck (PowerPoint)',
    description:
      'Presentation slides following your agenda order — one section per agenda item with charts and key metrics. Ready to present on screen.',
    icon: <FilePptOutlined className="text-2xl" />,
  },
  {
    id: 'email',
    title: 'Pre-Meeting Email Draft',
    description:
      'Professional email to send before the meeting with agenda overview and key highlights. Sets context for the client.',
    icon: <MailOutlined className="text-2xl" />,
  },
  {
    id: 'video',
    title: 'Video Avatar Summary',
    description:
      'AI avatar walks through your meeting analysis — send as a follow-up after the meeting, or as an alternative for clients who cannot attend.',
    icon: <VideoCameraOutlined className="text-2xl" />,
  },
]

export type ExportEstimate = {
  label: string
  /** Upper bound in minutes — decides which estimate the summary message quotes. */
  maxMinutes: number
}

export const exportEstimates: Record<AdvisoryAiExportFormat, ExportEstimate> = {
  pdf: { label: 'about 1 minute', maxMinutes: 1 },
  'pdf-summary': { label: 'about 1 minute', maxMinutes: 1 },
  ppt: { label: 'about 5 minutes', maxMinutes: 5 },
  email: { label: 'about 1 minute', maxMinutes: 1 },
  video: { label: '10–30 minutes', maxMinutes: 30 },
}

export const estimatedGenerationTimeSummary =
  'Estimated generation time: ~1 min for the PDFs and email draft, ~5 min for the slide deck, 10–30 min for video'

function slowestEstimate(types: AdvisoryAiExportFormat[]): ExportEstimate | null {
  return types.reduce<ExportEstimate | null>((worst, type) => {
    const estimate = exportEstimates[type]
    if (!estimate) return worst
    return !worst || estimate.maxMinutes > worst.maxMinutes ? estimate : worst
  }, null)
}

/**
 * Quotes the slowest output still running, never an average — promising a minute
 * while the video is queued is the one way this message can mislead. Video is
 * split out so four documents that land in five minutes aren't reported behind it.
 */
export function buildBackgroundMessage(inProgress: AdvisoryAiExportFormat[]): string {
  if (inProgress.length === 0) return ''

  const documentEstimate = slowestEstimate(inProgress.filter((type) => type !== 'video'))
  if (inProgress.includes('video') && documentEstimate) {
    return `Your documents will be ready in ${documentEstimate.label}; the video avatar summary takes ${exportEstimates.video.label}. You can come back later — these keep running in the background.`
  }

  const estimate = slowestEstimate(inProgress)
  if (!estimate) return ''
  return `You can come back in ${estimate.label} — this keeps running in the background.`
}

export function toExportStatus(status?: string | null): AdvisoryExportStatus | '' {
  const normalized = String(status || '').toUpperCase()
  if (normalized === AdvisoryExportStatus.PENDING_EXPORT) return AdvisoryExportStatus.PENDING_EXPORT
  if (normalized === AdvisoryExportStatus.EXPORTING) return AdvisoryExportStatus.EXPORTING
  if (normalized === AdvisoryExportStatus.COMPLETED_EXPORT) return AdvisoryExportStatus.COMPLETED_EXPORT
  if (normalized === AdvisoryExportStatus.ERROR) return AdvisoryExportStatus.ERROR
  return ''
}

export function buildExportFilename({
  exportType,
  orgToken,
  meetingDateToken,
}: {
  exportType: AdvisoryAiExportFormat
  orgToken: string
  meetingDateToken: string
}): string {
  const baseName = exportBaseNames[exportType] ?? 'Adviceprep'
  return `${baseName}-${orgToken}-${meetingDateToken}`
}
